import React, { createContext, useContext, useReducer } from "react";
import AdjuntoReducer from "../reducers/AdjuntoReducer";
import AdjuntosService from "../services/AdjuntosService";
import {
  SET_FOCUSED,
  SET_ADJUNTOS,
  SET_SINGLE_ADJUNTO,
  SET_PROPERTY_ADJUNTO,
  SET_SINGLE_ADJUNTO_PARAMS,
} from "../types/adjuntos";
import { ModalContext } from "./ModalContext";
import { AppConfigContext } from "./AppConfigContext";
import { HIDE_SPINNER, SHOW_SPINNER } from "../actions/types";

const initialState = {
  adjunto: null,
  adjuntos: null,
  spinner: false,
};

export const AdjuntosContext = createContext(initialState);

export const AdjuntosProvider = ({ children }) => {
  const [state, dispatch] = useReducer(AdjuntoReducer, initialState);

  const { alert, success, clearModal } = useContext(ModalContext);

  const { S3_ENDPOINT } = useContext(AppConfigContext);

  // AdjuntosContext.js
  const getAdjuntoProyecto = (idProyecto, idAdjunto, callback, signal) => {
    const fetchData = async () => {
      try {
        const res = await AdjuntosService.getAdjuntoFolder(
          idProyecto,
          idAdjunto,
          { signal }
        );
        const { adjunto } = res.data;
        let src = `${S3_ENDPOINT}/files/proyectos/${adjunto.idAdjunto}.${adjunto.tipo}`;
        if (adjunto.content) {
          src = `data:image/${adjunto.tipo};base64,${adjunto.content}`;
        }
        if (typeof callback === "function") {
          callback(src);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error al obtener adjunto:", error);
          callback(null);
        }
      }
    };

    fetchData();
  };

  const setAdjuntos = (adjuntos) => {
    dispatch({ type: SET_ADJUNTOS, payload: adjuntos });
  };

  const setFocused = (adjunto) => {
    dispatch({ type: SET_FOCUSED, payload: adjunto });
  };

  const setSingleAdjunto = (adjunto, params) => {
    dispatch({ type: SET_SINGLE_ADJUNTO, payload: adjunto });
    dispatch({ type: SET_SINGLE_ADJUNTO_PARAMS, payload: params });
  };

  const clearSingleAdjunto = () => {
    dispatch({ type: SET_SINGLE_ADJUNTO, payload: null });
    dispatch({ type: SET_SINGLE_ADJUNTO_PARAMS, payload: {} });
  };

  const uploadSingleAdjunto = (idProyecto, idFolder, file) => {
    const formData = AdjuntosService.getFormData(file);
    // Without a folder the file goes to /adjuntos, which only takes the
    // FormData (passing idProyecto first sent it as the request body).
    const request =
      !idFolder || idFolder === null
        ? AdjuntosService.postAdjunto(formData)
        : AdjuntosService.postAdjuntoFolder(idProyecto, idFolder, formData);

    return new Promise((resolve, reject) => {
      request
        .then(res => {
          const { data } = res;
          resolve(data);
        })
        .catch(err => {
          console.log(err);
          reject(err)
        })
    })
  };

  const uploadAdjuntos = (idProyecto, idFolder, files, callback, renameTo) => {
    dispatch({ type: SHOW_SPINNER });
    let promises = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = AdjuntosService.getFormData(file);
      promises.push(
        AdjuntosService.postAdjuntoFolder(idProyecto, idFolder, formData).then(res => {
          if(renameTo && renameTo !== null) {
            const { idAdjunto } = res;
            AdjuntosService.putAdjunto(idAdjunto, renameTo);
          }
        })
      );
    }

    return new Promise((resolve, reject) => {
      Promise.all(promises)
      .then(() => {
        clearModal();
        dispatch({ type: HIDE_SPINNER });
        success("Archivos guardados con éxito.");
        if (typeof callback === "function") callback();
        resolve()
      })
      .catch((error) => {
        dispatch({ type: HIDE_SPINNER });
        alert(error);
        reject(error)
      });
    })

  };

  const setPropertyAdjunto = (idAdjunto, key, value) => {
    dispatch({ type: SET_PROPERTY_ADJUNTO, payload: { idAdjunto, key, value } });
  };

  const deleteAdjunto = (idProyecto, idAdjunto, callback) => {
    AdjuntosService.deleteAdjuntoFolder(idProyecto, idAdjunto)
      .then(() => {
        if (callback) callback();
        success("Adjunto eliminado con éxito.");
      })
      .catch((error) => {
        alert(error);
      });
  };

  const updateSingleAdjunto = (idProyecto, idAdjunto, nombre, callback) => {
    AdjuntosService.putAdjunto(idProyecto, idAdjunto, nombre)
      .then(() => {
        if (callback) callback();
        success("Adjunto actualizado.");
      })
      .catch((error) => {
        alert(error);
      });
  };

  const downloadAdjunto = (idProyecto, adjunto) => {
    const { idAdjunto } = adjunto;
    return AdjuntosService.downloadAdjunto(idProyecto, idAdjunto)
      .then((res) => {
        // Keep the file's type so browsers (iOS) keep its extension.
        const tipo = res.headers?.["content-type"] || res.data.type;
        const url = window.URL.createObjectURL(
          new Blob([res.data], tipo ? { type: tipo } : undefined)
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${adjunto.nombre}.${adjunto.tipo}`);
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        alert(error);
      });
  };

  return (
    <AdjuntosContext.Provider
      value={{
        ...state,
        setFocused,
        setAdjuntos,
        deleteAdjunto,
        uploadAdjuntos,
        downloadAdjunto,
        setSingleAdjunto,
        setPropertyAdjunto,
        clearSingleAdjunto,
        getAdjuntoProyecto,
        updateSingleAdjunto,
        uploadSingleAdjunto,
      }}
    >
      {children}
    </AdjuntosContext.Provider>
  );
};
